// noinspection DuplicatedCode

import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import TipNavbar from "../../components/TipNavbar";

export default function TipSuccessPageV3() {
    const [searchParams] = useSearchParams();
    const sessionID = searchParams.get("session_id"); // ✅ Stripe Checkout ID
    const tipIDParam = searchParams.get("tipID");

    const [tipData, setTipData] = useState(null);
    const [employeeName, setEmployeeName] = useState(null);
    const [employeeImage, setEmployeeImage] = useState(null);
    const [businessName, setBusinessName] = useState(null);
    const [googlePlaceID, setGooglePlaceID] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            let tipID = null;

            try {
                if (sessionID) {
                    console.log("Stripe session ID detected");
                    tipID = sessionID;
                    const tipSnap = await getDoc(doc(db, "payment_links", tipID)); // ✅ Same as before
                    if (!tipSnap.exists()) throw new Error("Tip not found.");

                    const tip = tipSnap.data();
                    setTipData(tip);
                    setBusinessName(tip.businessName || null);
                    if (tip.googlePlaceID) setGooglePlaceID(tip.googlePlaceID);
                    setEmployeeName(tip.employeeName || tip.employeeID);
                    setEmployeeImage(tip.employeeImgUrl || null);

                } else if (tipIDParam) {
                    console.log("TipID parameter used");
                    tipID = tipIDParam;
                    const tipSnap = await getDoc(doc(db, "tips", tipID));

                    if (!tipSnap.exists()) throw new Error("Tip not found.");

                    const tip = tipSnap.data();
                    setTipData(tip);
                    setBusinessName(tip.businessName || null);
                    if (tip.googlePlaceID) setGooglePlaceID(tip.googlePlaceID);
                    setEmployeeName(tip.employeeName || tip.employeeID);
                    setEmployeeImage(tip.employeeImgUrl || null);

                } else {
                    throw new Error("No valid parameter provided.");
                }

            } catch (err) {
                console.error("Error:", err);
                setError(err.message || "Something went wrong.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [sessionID, tipIDParam]);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-screen text-gray-500 text-lg">
                <div className="animate-spin rounded-full h-8 w-8 border-[3px] border-gray-300 border-t-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return <div className="flex items-center justify-center h-screen text-red-500 text-lg">{error}</div>;
    }

    if (!tipData) {
        return <div className="flex items-center justify-center h-screen text-gray-600 text-lg">No tip data found.</div>;
    }

    const timestamp = tipData.createdAt?.toDate();
    const time = timestamp?.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    const date = timestamp?.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });

    const netAmount = Number(tipData.amount);
    const originalGross = ((netAmount + 0.25) / 0.93).toFixed(2); // Optional: update if Stripe has different fees

    return (
        <div className="relative min-h-screen bg-gray-100 overflow-hidden bg-gradient-to-b from-[#0fc] to-[#7ebdff]">
            <div className="relative z-20">
                <TipNavbar />
            </div>

            <div className="relative z-10 flex flex-col items-center pt-8 px-2">
                {/* Big avatar with gradient ring (V1 style) */}
                {employeeImage && (
                    <div className="mx-auto w-48 h-48 rounded-full p-[8px] bg-gradient-to-br from-cyan-400 to-emerald-400 z-0 relative">
                        <img
                            src={employeeImage}
                            alt={employeeName}
                            className="w-full h-full rounded-full object-cover"
                        />
                    </div>
                )}

                <h1 className="text-3xl font-extrabold mb-4 max-w-48 uppercase text-center mt-4">
                    {tipData.amount > 0 ? "Thank you for tipping" : "Thanks for your feedback!"}
                </h1>

                {/* Name box — rounded-xl to match tip page */}
                <div className="w-full left-0 right-0 z-10 mb-4">
                    <div className="bg-blue-500 text-white max-w-md mx-auto font-extrabold text-lg py-2 rounded-xl shadow text-center w-full">
                        {employeeName?.toUpperCase()}
                    </div>
                </div>

                {/* ─── CARD ─── */}
                <div className="bg-white w-full max-w-md p-5 rounded-2xl shadow-lg shadow-black/10 text-center">
                    {/* Tip amount */}
                    {tipData.amount > 0 && (
                        <p className="text-lg font-bold text-gray-900 mb-3">
                            You tipped <span className="text-2xl font-extrabold">&euro;{originalGross}</span>
                        </p>
                    )}

                    {/* Rating */}
                    {tipData.rating > 0 && (
                        <div className="flex items-center justify-center gap-1 mb-3">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <svg
                                    key={star}
                                    className={`w-7 h-7 ${tipData.rating >= star ? "text-yellow-400 drop-shadow-sm" : "text-gray-300"}`}
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                            ))}
                        </div>
                    )}

                    {/* Feedback */}
                    {tipData.feedback && (
                        <div className="bg-gray-50 rounded-xl px-4 py-3 mb-3">
                            <p className="text-sm text-gray-600 italic">"{tipData.feedback}"</p>
                        </div>
                    )}

                    {/* Timestamp */}
                    <p className="text-xs text-gray-400 mb-4">Payment processed via Stripe</p>
                    {time && date && (
                        <p className="text-xs text-gray-400 mb-4">{time} &middot; {date}</p>
                    )}

                    {/* Google Review button */}
                    {googlePlaceID && (
                        <a
                            href={`https://search.google.com/local/writereview?placeid=${googlePlaceID}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2.5 w-full py-3 bg-greenmint hover:bg-greenmint/90 text-black font-extrabold text-lg rounded-xl transition-all hover:shadow-md"
                        >
                            <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                            </svg>
                            Leave a Google Review
                        </a>
                    )}
                </div>
            </div>

            <div className="relative z-10 text-xs text-center text-gray-600 px-4 py-6 leading-snug">
                <p className="mt-1">
                    Please contact our support at support@tiply.ee in case of any issues.
                </p>
            </div>
        </div>
    );
}
